// Language context and translations for GrowPlay Facilitator App
import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // App Title
    appTitle: "GrowPlay Facilitator",
    appSubtitle: "Evaluation & Motivation System",
    
    // Navigation
    dashboard: "Dashboard",
    events: "Events",
    evaluations: "Evaluations",
    myEvaluations: "My Evaluations",
    teamView: "Team View",
    leaderboard: "Leaderboard",
    trainingRequests: "Training Requests",
    adminPanel: "Admin Panel",
    profile: "Profile",
    settings: "Settings",
    logout: "Logout",
    
    // Auth
    login: "Login",
    register: "Register",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    name: "Full Name",
    loginWithGoogle: "Continue with Google",
    orContinueWith: "Or continue with",
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    signUp: "Sign Up",
    signIn: "Sign In",
    
    // Roles
    admin: "Admin",
    seniorFacilitator: "Senior Facilitator",
    facilitator: "Facilitator",
    
    // Status
    statusGreen: "Excellent",
    statusYellow: "Acceptable",
    statusRed: "Needs Improvement",
    
    // Events
    newEvent: "New Event",
    editEvent: "Edit Event",
    eventName: "Event Name",
    eventType: "Event Type",
    teambuilding: "Teambuilding",
    training: "Training Workshop",
    eventDate: "Date",
    location: "Location",
    description: "Description",
    assignFacilitators: "Assign Facilitators",
    assignSeniorFacilitator: "Assign Senior Facilitator",
    
    // Evaluations
    newEvaluation: "New Evaluation",
    selfEvaluation: "Self Evaluation",
    peerEvaluation: "Peer Evaluation",
    supervisorEvaluation: "Senior Evaluation",
    facilitatorEvaluation: "Facilitator Evaluation",
    evaluationType: "Evaluation Type",
    selectEvent: "Select Event",
    selectFacilitator: "Select Facilitator",
    submitEvaluation: "Submit Evaluation",
    averageScore: "Average Score",
    
    // Categories
    communicationEnergy: "Communication & Energy Management",
    teamHandlingSafety: "Team Handling & Safety",
    professionalismReliability: "Professionalism & Reliability",
    operationalExecution: "Operational Execution",
    facilitationSkills: "GrowPlay® Facilitation Skills",
    
    // Criteria
    clearInstructions: "Clear instruction communication",
    positiveEnergy: "Positive energy, smile, enthusiasm",
    timeManagement: "Management of time and activity flow",
    teamEngagement: "Team engagement/activation",
    participantSafety: "Participant safety (physical & psychological)",
    crisisManagement: "Management of difficult behaviors/crises",
    timelyPresence: "Timely presence - adherence to obligations",
    appearance: "Appearance and adherence to dress code",
    collaboration: "Collaboration with Coordinator & Operations",
    setupEquipment: "Setup & equipment - correct setup/tidiness",
    procedureAdherence: "Adherence to procedures",
    equipmentManagement: "Professional equipment management",
    educationalGoals: "Guiding team towards educational goals",
    theoryPractice: "Connecting theory with practice",
    debriefingSkills: "Debriefing skills / reflective conversation",
    
    // Comments
    generalComments: "General Comments",
    addComment: "Add Comment",
    microTrainingsRequested: "Micro Trainings Requested",
    selectTrainings: "Select trainings you would like to receive",
    
    // Time Periods
    thisMonth: "This Month",
    quarter: "Quarter",
    semester: "Semester",
    year: "Year",
    twoYears: "2 Years",
    allTime: "All Time",
    
    // Awards
    mvpAwards: "MVP Awards",
    mvpFacilitator: "MVP Facilitator",
    bestEnergy: "Best Energy Award",
    bestDebriefer: "Best Debriefer",
    bestReliability: "Best Reliability",
    
    // Actions
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    export: "Export",
    exportExcel: "Export to Excel",
    exportPdf: "Export to PDF",
    filter: "Filter",
    search: "Search",
    
    // Messages
    loading: "Loading...",
    noData: "No data available",
    noEvaluations: "No evaluations found",
    noEvents: "No events found",
    success: "Success!",
    error: "Error",
    confirmDelete: "Are you sure you want to delete this?",
    
    // Dashboard
    totalEvaluations: "Total Evaluations",
    totalEvents: "Total Events",
    yourScore: "Your Score",
    recentEvaluations: "Recent Evaluations",
    topPerformers: "Top Performers",
    
    // Score labels
    score1: "Needs Improvement",
    score2: "Below Average",
    score3: "Average",
    score4: "Good",
    score5: "Excellent",
  },
  el: {
    // App Title
    appTitle: "GrowPlay Facilitator",
    appSubtitle: "Σύστημα Αξιολόγησης & Κινήτρων",
    
    // Navigation
    dashboard: "Πίνακας Ελέγχου",
    events: "Εκδηλώσεις",
    evaluations: "Αξιολογήσεις",
    myEvaluations: "Οι Αξιολογήσεις μου",
    teamView: "Προβολή Ομάδας",
    leaderboard: "Κατάταξη",
    trainingRequests: "Αιτήματα Εκπαίδευσης",
    adminPanel: "Διαχείριση",
    profile: "Προφίλ",
    settings: "Ρυθμίσεις",
    logout: "Αποσύνδεση",
    
    // Auth
    login: "Σύνδεση",
    register: "Εγγραφή",
    email: "Email",
    password: "Κωδικός",
    confirmPassword: "Επιβεβαίωση Κωδικού",
    name: "Ονοματεπώνυμο",
    loginWithGoogle: "Συνέχεια με Google",
    orContinueWith: "Ή συνεχίστε με",
    dontHaveAccount: "Δεν έχετε λογαριασμό;",
    alreadyHaveAccount: "Έχετε ήδη λογαριασμό;",
    signUp: "Εγγραφή",
    signIn: "Σύνδεση",
    
    // Roles
    admin: "Διαχειριστής",
    seniorFacilitator: "Senior Facilitator",
    facilitator: "Facilitator",
    
    // Status
    statusGreen: "Άριστο",
    statusYellow: "Επαρκές",
    statusRed: "Χρειάζεται Βελτίωση",
    
    // Events
    newEvent: "Νέα Εκδήλωση",
    editEvent: "Επεξεργασία Εκδήλωσης",
    eventName: "Όνομα Εκδήλωσης",
    eventType: "Τύπος Εκδήλωσης",
    teambuilding: "Teambuilding",
    training: "Training Workshop",
    eventDate: "Ημερομηνία",
    location: "Τοποθεσία",
    description: "Περιγραφή",
    assignFacilitators: "Ανάθεση Facilitators",
    assignSeniorFacilitator: "Ανάθεση Senior Facilitator",
    
    // Evaluations
    newEvaluation: "Νέα Αξιολόγηση",
    selfEvaluation: "Αυτοαξιολόγηση",
    peerEvaluation: "Αξιολόγηση Ομοτίμου",
    supervisorEvaluation: "Αξιολόγηση Senior",
    facilitatorEvaluation: "Αξιολόγηση Facilitator",
    evaluationType: "Τύπος Αξιολόγησης",
    selectEvent: "Επιλογή Εκδήλωσης",
    selectFacilitator: "Επιλογή Facilitator",
    submitEvaluation: "Υποβολή Αξιολόγησης",
    averageScore: "Μέσος Όρος",
    
    // Categories
    communicationEnergy: "Επικοινωνία & Διαχείριση Ενέργειας",
    teamHandlingSafety: "Διαχείριση Ομάδας & Ασφάλεια",
    professionalismReliability: "Επαγγελματισμός & Αξιοπιστία",
    operationalExecution: "Λειτουργική Εκτέλεση",
    facilitationSkills: "Δεξιότητες Facilitation GrowPlay®",
    
    // Criteria
    clearInstructions: "Σαφής επικοινωνία οδηγιών",
    positiveEnergy: "Θετική ενέργεια, χαμόγελο, ενθουσιασμός",
    timeManagement: "Διαχείριση χρόνου και ροής δραστηριότητας",
    teamEngagement: "Εμπλοκή/Ενεργοποίηση ομάδας",
    participantSafety: "Ασφάλεια συμμετεχόντων (σωματική & ψυχολογική)",
    crisisManagement: "Διαχείριση δύσκολων συμπεριφορών/κρίσεων",
    timelyPresence: "Έγκαιρη παρουσία - τήρηση υποχρεώσεων",
    appearance: "Εμφάνιση και τήρηση κώδικα ντυσίματος",
    collaboration: "Συνεργασία με Συντονιστή & Λειτουργίες",
    setupEquipment: "Εγκατάσταση & εξοπλισμός - σωστή τοποθέτηση/τάξη",
    procedureAdherence: "Τήρηση διαδικασιών",
    equipmentManagement: "Επαγγελματική διαχείριση εξοπλισμού",
    educationalGoals: "Καθοδήγηση ομάδας προς εκπαιδευτικούς στόχους",
    theoryPractice: "Σύνδεση θεωρίας με πράξη",
    debriefingSkills: "Δεξιότητες debriefing / αναστοχαστική συζήτηση",
    
    // Comments
    generalComments: "Γενικά Σχόλια",
    addComment: "Προσθήκη Σχολίου",
    microTrainingsRequested: "Αιτούμενα Micro Trainings",
    selectTrainings: "Επιλέξτε εκπαιδεύσεις που θα θέλατε να λάβετε",
    
    // Time Periods
    thisMonth: "Αυτόν τον Μήνα",
    quarter: "Τρίμηνο",
    semester: "Εξάμηνο",
    year: "Έτος",
    twoYears: "2 Έτη",
    allTime: "Όλες οι Περίοδοι",
    
    // Awards
    mvpAwards: "Βραβεία MVP",
    mvpFacilitator: "MVP Facilitator",
    bestEnergy: "Βραβείο Καλύτερης Ενέργειας",
    bestDebriefer: "Καλύτερος Debriefer",
    bestReliability: "Καλύτερη Αξιοπιστία",
    
    // Actions
    save: "Αποθήκευση",
    cancel: "Ακύρωση",
    delete: "Διαγραφή",
    edit: "Επεξεργασία",
    view: "Προβολή",
    export: "Εξαγωγή",
    exportExcel: "Εξαγωγή σε Excel",
    exportPdf: "Εξαγωγή σε PDF",
    filter: "Φίλτρο",
    search: "Αναζήτηση",
    
    // Messages
    loading: "Φόρτωση...",
    noData: "Δεν υπάρχουν δεδομένα",
    noEvaluations: "Δεν βρέθηκαν αξιολογήσεις",
    noEvents: "Δεν βρέθηκαν εκδηλώσεις",
    success: "Επιτυχία!",
    error: "Σφάλμα",
    confirmDelete: "Είστε σίγουροι ότι θέλετε να διαγράψετε;",
    
    // Dashboard
    totalEvaluations: "Συνολικές Αξιολογήσεις",
    totalEvents: "Συνολικές Εκδηλώσεις",
    yourScore: "Η Βαθμολογία σας",
    recentEvaluations: "Πρόσφατες Αξιολογήσεις",
    topPerformers: "Κορυφαίοι Performers",
    
    // Score labels
    score1: "Χρειάζεται Βελτίωση",
    score2: "Κάτω από το Μέσο Όρο",
    score3: "Μέτριο",
    score4: "Καλό",
    score5: "Άριστο",
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('el');

  const t = useCallback((key) => {
    return translations[language][key] || key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'el' ? 'en' : 'el');
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default translations;
